import React from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { gbp } from "../../lib/format";

/* LT-19c — the "Undo this import?" confirmation. Shows the plan the server
 * computed a moment ago; nothing is written until Confirm. */
export function ImportUndoDialog({ plan, open, onCancel, onConfirm, busy }) {
  const s = plan?.summary || {};
  const leftAlone = (plan?.plan || []).filter((p) => p.op === "skip" || p.op === "partial");
  const resume = plan?.batch?.status === "rollback_failed";
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && !busy && onCancel()}>
      <AlertDialogContent className="max-w-xl" data-testid="import-undo-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{resume ? "Resume undo of this import?" : "Undo this import?"}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-ink-2" style={{ fontSize: 12.5 }}>
              <div className="rounded border border-line bg-surface-2/40 px-3 py-2 font-mono tnum" data-testid="import-undo-summary">
                {s.prices_back ?? 0} price{s.prices_back === 1 ? "" : "s"} walked back
                {" · "}{s.fields_back ?? 0} other field{s.fields_back === 1 ? "" : "s"} put back
                {" · "}{s.archive ?? 0} created material{s.archive === 1 ? "" : "s"} archived
                {" · "}{(s.left_alone ?? 0) + (s.partial ?? 0)} row{(s.left_alone ?? 0) + (s.partial ?? 0) === 1 ? "" : "s"} changed since — left alone
                {s.already_done ? ` · ${s.already_done} already done` : ""}
              </div>
              <p>
                Every price walked back gets a new history line (new → old, marked <i>import rollback</i>) — nothing in the history is removed.
                Materials this import created are archived, not deleted. There is no undo of the undo: to bring these prices back, upload the file again.
              </p>
              {s.legacy && (
                <p className="text-amber-800" data-testid="import-undo-legacy-note">
                  This batch was applied before undo existed, so only prices can be walked back (from the price history). Coverage, wastage,
                  keywords and supplier references it set were not recorded and stay as imported.
                </p>
              )}
              {leftAlone.length > 0 && (
                <div data-testid="import-undo-left-alone">
                  <div className="text-ink-3 uppercase" style={{ fontSize: 10.5, letterSpacing: 0.4 }}>Left alone — changed since the import</div>
                  <ul className="mt-1 max-h-40 overflow-auto font-mono" style={{ fontSize: 11 }}>
                    {leftAlone.map((p) => (
                      <li key={p.row_number}>row {p.row_number} {p.material_code || ""} {p.material_name}: {p.note}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(plan?.plan || []).filter((p) => p.price).slice(0, 8).map((p) => (
                <div key={p.row_number} className="font-mono text-ink-3" style={{ fontSize: 11 }}>
                  {p.material_code} {gbp(p.price.from)} → {gbp(p.price.to)}
                </div>
              ))}
              {(plan?.plan || []).filter((p) => p.price).length > 8 && (
                <div className="text-ink-4" style={{ fontSize: 11 }}>… and {(plan.plan || []).filter((p) => p.price).length - 8} more</div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="import-undo-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={busy} data-testid="import-undo-confirm">
            {busy ? <Loader2 size={14} className="animate-spin" /> : (resume ? "Resume undo" : "Undo this import")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
